import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '@/hooks/reduxHooks';
import { toSafeMessage, type AgentClient } from './agentClient';
import type { AgentsPageAccess } from './types';

export type AgentsPageAccessState = {
  status: 'off' | 'loading' | 'ready' | 'error';
  access: AgentsPageAccess | null;
  error: string | null;
};

const OFF: AgentsPageAccessState = { status: 'off', access: null, error: null };
const LOADING: AgentsPageAccessState = {
  status: 'loading',
  access: null,
  error: null,
};

/**
 * Asks the agent service whether this user may open the Agents page. Refetches
 * when the client or the session token changes; aborts on unmount. A null
 * client (service off, or the dev mock still loading) reads as 'off'.
 */
export function useAgentsPageAccess(
  client: AgentClient | null,
): AgentsPageAccessState & { retry: () => void } {
  const token = useAppSelector((s) => s.auth.token);
  const [state, setState] = useState<AgentsPageAccessState>(LOADING);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!client) {
      setState(OFF);
      return;
    }
    const controller = new AbortController();
    setState(LOADING);
    client
      .getAgentsPageAccess(controller.signal)
      .then((access) => {
        if (!controller.signal.aborted) {
          setState({ status: 'ready', access, error: null });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({ status: 'error', access: null, error: toSafeMessage(err) });
      });
    return () => controller.abort();
  }, [client, token, attempt]);

  return { ...state, retry };
}
